/**
 * Footer 컴포넌트
 * Figma Design v03 스펙에 따른 푸터 디자인 (반응형)
 *
 * 디자인 스펙:
 * - 배경: #f8f9f8
 * - 높이: 80px
 * - 상단 테두리: 1px solid #e6e6e6
 * - Logo + 주소/연락처 + Copyright
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

export interface FooterProps {
  /** Footer 높이 (기본값: 80px) */
  height?: number;
}

/**
 * Dream Logistics TMS Footer 컴포넌트
 *
 * Figma Design v03 스펙:
 * - 배경색: #f8f9f8
 * - 높이: 80px
 * - 상단 테두리: 1px solid #e6e6e6
 * - 패딩: 0 48px
 * - 3개 섹션: Logo (80x80) + 주소/연락처 + Copyright
 * - 반응형: Flexbox 기반 레이아웃
 *
 * @component
 * @example
 * // 기본 사용
 * <Footer />
 *
 * @example
 * // 커스텀 높이
 * <Footer height={100} />
 */
export const Footer: React.FC<FooterProps> = ({ height = 80 }) => {
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#f8f9f8',
        borderTop: '1px solid #e6e6e6',
        minHeight: `${height}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: '16px', sm: '24px', md: '48px' },
        py: { xs: '16px', md: 0 },
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1342px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: '16px', sm: '24px', md: '32px' },
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: '60px', md: '80px' },
            height: { xs: '60px', md: '80px' },
          }}
        >
          <Box
            component="img"
            src="/assets/images/footer_logo.png"
            alt="Dream Logistics Logo"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: '50% 50%',
            }}
          />
        </Box>

        {/* 주소 및 연락처 */}
        <Box
          sx={{
            flex: 1,
            minWidth: { xs: '100%', sm: '300px', md: 0 },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
              fontSize: { xs: '11px', sm: '12px', md: '13px' },
              lineHeight: '20px',
              color: '#8c8c8c',
              fontWeight: 400,
              whiteSpace: { xs: 'normal', md: 'pre-wrap' },
            }}
          >
            경기도 평택시 도일유통길 20-7(도일동){' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              T
            </Box>{' '}
            031-647-4101{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              F
            </Box>{' '}
            031-647-4106
          </Typography>
        </Box>

        {/* Copyright */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            minWidth: { xs: '100%', md: 0 },
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Noto Sans', 'Noto Sans Symbols', sans-serif",
              fontSize: { xs: '11px', sm: '12px', md: '13px' },
              lineHeight: '20px',
              color: '#8c8c8c',
              fontWeight: 400,
              whiteSpace: { xs: 'normal', md: 'nowrap' },
            }}
          >
            Copyright ⓒ www.dreamlogis.com. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
